import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from "react-redux";

// Material-UI Core
import { LinearProgress, styled, Box, CardMedia } from '@mui/material';
import Carousel from 'react-material-ui-carousel';

// Custom Components
import SmartCarouselItem from './SmartCarouselItem'

// Custom Utilities
import {getFieldFormatters} from '../../../../utilities/DataSourceUtilities';
import {formatValue} from '../../../../utilities/FormatUtilities';

import useDataSource from '../../../../hooks/useDataSource';
import useMainCanvasSize from "../../../../hooks/useMainCanvasSize";
import useWindowSize from "../../../../hooks/useWindowSize";
import NoImage from "../../../../styles/images/NoProjectImageFound.jpg";


const PREFIX = 'SmartCarousel';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    grayBg: `${PREFIX}-grayBg`,
    lightGrayBg: `${PREFIX}-lightGrayBg`,
	useMeNotThemeLight: `${PREFIX}-useMeNotThemeLight`,
    useMeNotThemeDark: `${PREFIX}-useMeNotThemeDark`,
    media: `${PREFIX}-media`
};

const StyledBox = styled(Box)((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: '100%' //'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.grayBg}`]: {
		// height: '100%',
		backgroundColor: "rgb(51, 50, 50, .90)" //"#333232",
	},

    [`& .${classes.lightGrayBg}`]: {
		// height: '100%',
		backgroundColor: "rgb(103, 103, 103,.90)"
	},

    // Used for home page, basic role, carousel
	[`& .${classes.useMeNotThemeLight}`]: {
        backgroundImage: "linear-gradient(to top, rgba(224,224,224,1) 0%, rgba(238,238,238,1) 25%, rgba(245,245,245,1) 100%)",
        borderBottomRightRadius: "4px",
        borderBottomLeftRadius: "4px"
	},

    // Used for home page, basic role, carousel
    [`& .${classes.useMeNotThemeDark}`]: {
        backgroundColor: theme.palette.background.default,
        backgroundImage: 'linear-gradient(rgba(255, 255,255, 0.05), rgba(255, 255, 255, 0.05))',
        borderBottomRightRadius: "4px",
        borderBottomLeftRadius: "4px"
    },

    [`& .${classes.media}`]: {
		// height: '100%',
		borderTopRightRadius: '4px',
		borderTopLeftRadius: '4px',
	}
}));

export default function SmartCarousel({id, configuration, xId, onLoading, onLoaded}) {


    // https://jacobsengineering.sharepoint.com/sites/CPOV012053/HomeImages/Forms/HomeImages.aspx

    //     configuration: {
    //          dataSource: {
    //              name: <datasource name for image metadata>
    //              fields: {
    //                  identifier: <identifier field name from datasource>
    //                  title: <title field name from datasource>
    //                  description: <description field name from datasource>
    //                  date: <date field name from datasource>
    //                  buttonLabel: <button label field name from datasource>
    //                  buttonLink: <button link field name from datasource>
    //                  buttonLinkType: <button link type field name from datasource>
    //                  buttonEnabled: <button enabled field name from datasource>
    //                  seqNo: <sequence number field name from datasource>
    //              }
    //              imageTableName: <table name for image>
    //          },
    //          element: {
    //              carousel: {
    //                  animation: fade | slide
    //                  autoplay: true | false
    //                  indicatorsVisible: true | false
    //                  interval:
    //                  navButtonsVisible: true | false
    //              }
    //              carouselItem: {
    //                  title: {
    //                      display: true | false
    //                      color: initial | inherit | primary | secondary | textPrimary | textSecondary | error | HEX code
    //                      variant: h1 | h2 | h3 | h4 | h5 | h6 | subtitle1 | subtitle2 | body1 | body2 | caption | button | overline | srOnly | inherit
    //                  }
    //                  description: {
    //                      display: true | false
    //                      color: initial | inherit | primary | secondary | textPrimary | textSecondary | error | HEX code
    //                      variant: h1 | h2 | h3 | h4 | h5 | h6 | subtitle1 | subtitle2 | body1 | body2 | caption | button | overline | srOnly | inherit
    //                  }
    //                  image: {
    //                      size: small | medium | large (assumes SMImage, MDImage, LGImage are the field names)
    //                      backgroundSize: contain | cover
    //                  }
    //              }
    //          }
    //      }

	const persist = useSelector(state => state.persist);
	const isThemeLight = persist.themeLight;
	const componentUpdated = useSelector(state => state.page.componentUpdated);

    // DATASOURCE
    // -- DATASOURCE: Name
    const dataSourceName = configuration?.dataSource?.name ? configuration?.dataSource?.name : '';
    // -- DATASOURCE: Fields
    const identifierField = configuration?.dataSource?.fields?.identifier ? configuration?.dataSource?.fields?.identifier : '';
    const titleField = configuration?.dataSource?.fields?.title ? configuration?.dataSource?.fields?.title : '';
    const descriptionField = configuration?.dataSource?.fields?.description ? configuration?.dataSource?.fields?.description : '';
    const dateField = configuration?.dataSource?.fields?.date ? configuration?.dataSource?.fields?.date : '';
    const buttonLabelField = configuration?.dataSource?.fields?.buttonLabel ? configuration?.dataSource?.fields?.buttonLabel : '';
    const buttonLinkField = configuration?.dataSource?.fields?.buttonLink ? configuration?.dataSource?.fields?.buttonLink : '';
    const buttonLinkTypeField = configuration?.dataSource?.fields?.buttonLinkType ? configuration?.dataSource?.fields?.buttonLinkType : '';
    const buttonEnabledField = configuration?.dataSource?.fields?.buttonEnabled !== undefined ? configuration?.dataSource?.fields?.buttonEnabled : '';
    const seqNoField = configuration?.dataSource?.fields?.seqNo ? configuration?.dataSource?.fields?.seqNo : '';
    // -- DATASOURCE: Image Table Name
    const imageTableName = configuration?.dataSource?.imageTableName ? configuration?.dataSource?.imageTableName : '';
    const imageIDPublished = configuration?.dataSource?.imageIDPublished ? configuration?.dataSource?.imageIDPublished : '';
    const imageIDStaged = configuration?.dataSource?.imageIDStaged ? configuration?.dataSource?.imageIDStaged : '';
    // ELEMENT
    // -- STYLES
    const styles = configuration?.element?.styles;
    // -- ELEMENT: Carousel
    const carouselAnimation = configuration?.element?.carousel?.animation ? configuration?.element?.carousel?.animation : 'slide';
    const carouselAutoPlay = configuration?.element?.carousel?.autoPlay !== undefined ? configuration?.element?.carousel?.autoPlay : true;
    const carouselIndicatorsVisible = configuration?.element?.carousel?.indicatorsVisible !== undefined ? configuration?.element?.carousel?.indicatorsVisible : true;
    const carouselInterval = configuration?.element?.carousel?.interval ? configuration?.element?.carousel?.interval : 4000;
	const carouselTimeout = configuration?.element?.carousel?.timeout ? configuration?.element?.carousel?.timeout : 500;
    const carouselNavButtonsAlwaysVisible = configuration?.element?.carousel?.navButtonsVisible !== undefined ? configuration?.element?.carousel?.navButtonsVisible : true;
    // -- ELEMENT: Carousel Item: Title
    const carouselItemTitleDisplay = configuration?.element?.carouselItem?.title?.display !== undefined ? configuration?.element?.carouselItem?.title?.display : true;
    const carouselItemTitleColor = configuration?.element?.carouselItem?.title?.color ? configuration?.element?.carouselItem?.title?.color : 'textPrimary';
    const carouselItemTitleVariant = configuration?.element?.carouselItem?.title?.variant ? configuration?.element?.carouselItem?.title?.variant : 'h4';
    // -- ELEMENT: Carousel Item: Description
    const carouselItemDescriptionDisplay = configuration?.element?.carouselItem?.description?.display !== undefined ? configuration?.element?.carouselItem?.description?.display : true;
    const carouselItemDescriptionColor = configuration?.element?.carouselItem?.description?.color ? configuration?.element?.carouselItem?.description?.color : 'textSecondary';
    const carouselItemDescriptionVariant = configuration?.element?.carouselItem?.description?.variant ? configuration?.element?.carouselItem?.description?.variant : 'h6';
	// -- ELEMENT: Metadata Text Container
	const metadataTextContainerPosition = configuration?.element?.carouselItem?.metadataTextContainer?.position !== undefined ? configuration.element.carouselItem.metadataTextContainer.position.toLowerCase() : 'bottom';
	const metadataTextContainerTheme = configuration?.element?.carouselItem?.metadataTextContainer?.useTheme !== undefined ? configuration.element.carouselItem.metadataTextContainer.useTheme : true;
    // -- ELEMENT: Carousel Item: Image
    const carouselItemImageSize = configuration?.element?.carouselItem?.image?.size ? configuration?.element?.carouselItem?.image?.size : 'medium';
    const carouselItemImageBackgroundSize = configuration?.element?.carouselItem?.image?.backgroundSize ? configuration?.element?.carouselItem?.image?.backgroundSize : 'cover';
    // -- ELEMENT: Carousel Item : Button
    const carouselItemButtonColor = configuration?.element?.carouselItem?.button?.color ? configuration?.element?.carouselItem?.button?.color : '';
    const carouselItemButtonSize = configuration?.element?.carouselItem?.button?.size ? configuration?.element?.carouselItem?.button?.size : 'medium';
    const carouselItemButtonVariant = configuration?.element?.carouselItem?.button?.variant ? configuration?.element?.carouselItem?.button?.variant : 'contained';
    // -- ELEMENT: Carousel Item : Button Position
    const carouselItemButtonPositionAlignItems = configuration?.element?.carouselItem?.buttonPosition?.alignItems ? configuration?.element?.carouselItem?.buttonPosition?.alignItems : 'center';
    const carouselItemButtonPositionJustifyContent = configuration?.element?.carouselItem?.buttonPosition?.justifyContent ? configuration?.element?.carouselItem?.buttonPosition?.justifyContent : 'center';

    // App State
	const ref = useRef(null);
    const dataSource = useDataSource(dataSourceName);
	const size = useMainCanvasSize();
	const windowSize = useWindowSize();

    // Component State
    const [carouselItems, setCarouselItems] = useState([]);
	const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
	const [showFetchingImage, setShowFetchingImage] = useState(configuration.element?.showFetchingImage !== undefined ? (configuration.element.showFetchingImage ? NoImage : null) : NoImage);
	const [dynamicContainerHeight, setDynamicContainerHeight] = useState(ref?.current?.clientHeight !== undefined ? ref.current.clientHeight : styles?.root?.height);

    // Item properties passed to the SmartCarouselItem component
    const carouselItemConfiguration = {
        itemHeight: styles?.root?.height,
        // itemBackgroundColor: styles?.root?.backgroundColor,
        button: {
            dataSourceName: dataSourceName,
        },
        image: {
            imageTableName: imageTableName,
            imageIDPublished: imageIDPublished,
            imageIDStaged: imageIDStaged,
            identifierFieldName: identifierField,
            size: carouselItemImageSize,
            backgroundSize: carouselItemImageBackgroundSize
        },
        title: {
            display: carouselItemTitleDisplay,
            color: carouselItemTitleColor,
            variant: carouselItemTitleVariant,
        },
        description: {
            display: carouselItemDescriptionDisplay,
            color: carouselItemDescriptionColor,
            variant: carouselItemDescriptionVariant
        },
		metadataTextContainer: {
			position: metadataTextContainerPosition,
			useTheme: metadataTextContainerTheme
		}
    };

	// Loading
	useEffect(() => {
		if (dataSource.fetching === true) {
			//setShowFetchingImage(configuration.element?.showFetchingImage !== undefined ? (configuration.element.showFetchingImage ? NoImage : null) : NoImage);

			if (isFunction(onLoading)) {
				onLoading();
			}
		}
	}, [dataSource.fetching]);

	// Loaded
	useEffect(() => {
		if (dataSource.fetching === false && dataSource.fetched === true) {

			if (isFunction(onLoaded)) {
				onLoaded();
			}
		}
	}, [dataSource.fetched]);

	// Resized
	useEffect(() => {
		// console.log('SmartCarousel --> useEffect() [componentUpdated, ref.current]');
		// console.log('    xId: ', xId);
		// console.log('    componentUpdated.id: ', componentUpdated.id);
		// console.log('    ref.current.clientHeight: ', ref.current.clientHeight);
		// console.log('    dynamicContainerHeight + 20: ', parseInt(dynamicContainerHeight) + 20);

		if (xId) {
			if (componentUpdated.id === xId) {
				setDynamicContainerHeight(ref.current.clientHeight - 20);		// 20 is the height of the Indicators
			} else if (ref.current.clientHeight !== (parseInt(dynamicContainerHeight) + 20)) {	// Support for no itemHeight from config.json when isEditing
				setDynamicContainerHeight(ref.current.clientHeight - 20);		// 20 is the height of the Indicators
			}
		} else if (ref.current.clientHeight !== (parseInt(dynamicContainerHeight) + 20)) {	// Support for no itemHeight from config.json when !isEditing
			setDynamicContainerHeight(ref.current.clientHeight < parseInt(styles?.root?.height) ? (parseInt(styles?.root?.height) + 20) : (ref.current.clientHeight - 20));		// 20 is the height of the Indicators
		}
	}, [componentUpdated, ref.current, size, windowSize]);

    useEffect(() => {

        const data = [];

        if (dataSource.error) {
            // console.log('-----> SmartCarousel:useEffect(): dataSource -- ERROR');
            // TODO: Error component

        } else if (dataSource.data && dataSource.fetched) {
            // console.log('-----> SmartCarousel:useEffect(): dataSource -- FETCHED DATA');

            // Get formatters from datasource
            const identifierFormatters = getFieldFormatters(dataSource, identifierField);
            const titleFormatters = getFieldFormatters(dataSource, titleField);
            const descriptionFormatters = getFieldFormatters(dataSource, descriptionField);
            const dateFormatters = getFieldFormatters(dataSource, dateField);
            const seqNoFormatters = getFieldFormatters(dataSource, seqNoField);

            for (let i = 0; i < dataSource.data.length; i++) {
                data.push({
                    identifier: identifierField ? formatValue(dataSource.data[i][identifierField], identifierFormatters, null) : null,
                    title: titleField ? formatValue(dataSource.data[i][titleField], titleFormatters, null) : null,
                    description: descriptionField ? formatValue(dataSource.data[i][descriptionField], descriptionFormatters, null) : null,
                    date: dateField ? formatValue(dataSource.data[i][dateField], dateFormatters, null) : null,
                    buttonEnabled: buttonEnabledField ? dataSource.data[i][buttonEnabledField] : false,
                    buttonConfiguration: {
                        element: {
                            button: {
                                color: carouselItemButtonColor,
                                label: buttonLabelField ? dataSource.data[i][buttonLabelField] : 'Button',
                                size: carouselItemButtonSize,
                                variant: carouselItemButtonVariant
                            },
                             buttonPosition: {
                                alignItems: carouselItemButtonPositionAlignItems,
                                justifyContent: carouselItemButtonPositionJustifyContent
                            }
                        },
                        link: buttonLinkField ? dataSource.data[i][buttonLinkField] : null,
                        linkType: buttonLinkTypeField ? dataSource.data[i][buttonLinkTypeField] : ''
                    },
                    seqNo: seqNoField ? formatValue(dataSource.data[i][seqNoField], seqNoFormatters, null) : null,
                });
            }

            // Set component state
            setCarouselItems(data);

        } else {
            // console.log('-----> SmartCarousel:useEffect(): dataSource -- OTHER');
            // TODO: Error component
        }
    }, [dataSource.data]);

	function isFunction(functionToCheck) {
		return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
	}

    return (
        <StyledBox className={classes.root} ref={ref}>
            {showProgress && dataSource.fetching &&
            <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
            	<React.Fragment>
					{showFetchingImage && (!dataSource.fetched || dataSource.data.length === 0) &&
						<CardMedia className={classes.media} sx={{height: '100%'}} style={{backgroundSize: carouselItemImageBackgroundSize}} image={showFetchingImage}/>
					}
				</React.Fragment>
            }
			{dataSource.data &&
				<React.Fragment>
					{(dataSource.fetched && dataSource.data.length > 0) &&
							<Carousel animation={carouselAnimation}
									  fullHeightHover={false}
									  autoPlay={carouselAutoPlay}
									  indicators={carouselIndicatorsVisible}
									  indicatorContainerProps={{
										  style: {
											  backgroundColor: carouselItemConfiguration.metadataTextContainer.useTheme ? (isThemeLight ? 'rgb(103, 103, 103, .90)' : 'rgb(51, 50, 50, .90)') : '',
											  position: 'absolute',
											  bottom: '0px',
											  marginTop: '-5px'
										  }
									  }}
									  indicatorIconButtonProps={{
										  style: {
											  // color: 'blue'       // 3
										  }
									  }}
									  interval={carouselInterval}
									  timeout={carouselTimeout}
									  navButtonsAlwaysVisible={carouselNavButtonsAlwaysVisible}
									  sx={{ height: carouselIndicatorsVisible ? '100%' : dynamicContainerHeight }}
									  className={(carouselItemConfiguration.metadataTextContainer.useTheme ? (isThemeLight ? classes.lightGrayBg : classes.grayBg) : (isThemeLight ? classes.useMeNotThemeLight : classes.useMeNotThemeDark))}
							>
								{
									carouselItems.map((item, i) => <SmartCarouselItem key={i} carouselItem={item}
																					  configuration={carouselItemConfiguration}
																					  height={dynamicContainerHeight}/>)
								}
							</Carousel>
					}
				</React.Fragment>
			}
        </StyledBox>
    );
}
